import { rUniform } from "../../../core/util/Random";
import { MusicalNote } from "../MusicalNote";
import { Instrument } from "./Instrument";

/**
 * Snare drum instrument with metallic characteristics using persistent oscillators.
 *
 * Creates a realistic snare drum sound by combining multiple synthesis layers:
 * - Tonal body component (pitched oscillator for drum body)
 * - Tuned noise bands for metallic ring
 * - Filtered white noise for snare wires
 *
 * Uses persistent oscillators for better performance and natural voice-stealing behavior.
 */
export class SnareDrum extends Instrument {
  // Synthesis parameters
  private bodyFreq: number = 200;
  private bodyDecay: number = 0.08;
  private bodyMix: number = 0.3;
  private snareFilterFreq: number = 2000;
  private snareFilterQ: number = 1.5;
  private metalFreq1: number = 3500;
  private metalFreq2: number = 5000;
  private metalQ: number = 10;
  private metalMix: number = 0.2;
  private attackTime: number = 0.001;
  private defaultDuration: number = 0.15;

  // Persistent audio nodes
  private bodyOsc!: OscillatorNode; // Initialized in setupPersistentNodes
  private bodyGain!: GainNode; // Initialized in setupPersistentNodes
  private metalSource: AudioBufferSourceNode | null = null;
  private metalFilter1!: BiquadFilterNode; // Initialized in setupPersistentNodes
  private metalFilter2!: BiquadFilterNode; // Initialized in setupPersistentNodes
  private metalGain!: GainNode; // Initialized in setupPersistentNodes
  private snareSource: AudioBufferSourceNode | null = null;
  private snareFilter!: BiquadFilterNode; // Initialized in setupPersistentNodes
  private snareGain!: GainNode; // Initialized in setupPersistentNodes
  private masterEnvelope!: GainNode; // Initialized in setupPersistentNodes
  private currentStopId: number = 0;

  constructor(audioContext: AudioContext, output: GainNode) {
    super(audioContext, output);

    // Create persistent audio chain
    this.setupPersistentNodes();
    this.initializeParameters();
  }

  /**
   * Set up persistent audio nodes that stay alive for the instrument's lifetime.
   */
  private setupPersistentNodes(): void {
    // Master envelope controls overall snare output
    this.masterEnvelope = this.audioContext.createGain();
    this.masterEnvelope.gain.value = 0;
    this.masterEnvelope.connect(this.gain);

    // Layer 1: Tonal body oscillator
    this.bodyOsc = this.audioContext.createOscillator();
    this.bodyOsc.type = "triangle";
    this.bodyOsc.frequency.value = this.bodyFreq;
    this.bodyGain = this.audioContext.createGain();
    this.bodyGain.gain.value = this.bodyMix;
    this.bodyOsc.connect(this.bodyGain);
    this.bodyGain.connect(this.masterEnvelope);
    this.bodyOsc.start();

    // Layer 2: Metallic ring filters
    this.metalFilter1 = this.audioContext.createBiquadFilter();
    this.metalFilter1.type = "bandpass";
    this.metalFilter1.frequency.value = this.metalFreq1;
    this.metalFilter1.Q.value = this.metalQ;

    this.metalFilter2 = this.audioContext.createBiquadFilter();
    this.metalFilter2.type = "bandpass";
    this.metalFilter2.frequency.value = this.metalFreq2;
    this.metalFilter2.Q.value = this.metalQ;

    this.metalGain = this.audioContext.createGain();
    this.metalGain.gain.value = this.metalMix;
    this.metalFilter1.connect(this.metalFilter2);
    this.metalFilter2.connect(this.metalGain);
    this.metalGain.connect(this.masterEnvelope);

    // Layer 3: Snare wire filter
    this.snareFilter = this.audioContext.createBiquadFilter();
    this.snareFilter.type = "highpass";
    this.snareFilter.frequency.value = this.snareFilterFreq;
    this.snareFilter.Q.value = this.snareFilterQ;

    this.snareGain = this.audioContext.createGain();
    const snareMix = 1.0 - this.bodyMix - this.metalMix; // Remaining mix
    this.snareGain.gain.value = snareMix;
    this.snareFilter.connect(this.snareGain);
    this.snareGain.connect(this.masterEnvelope);

    // Create initial noise sources
    this.createMetalNoiseSource();
    this.createSnareNoiseSource();
  }

  /**
   * Create metallic noise source for layer 2.
   */
  private createMetalNoiseSource(): void {
    if (this.metalSource) {
      this.metalSource.disconnect();
    }

    const bufferSize = this.audioContext.sampleRate * 0.5;
    const buffer = this.audioContext.createBuffer(
      1,
      bufferSize,
      this.audioContext.sampleRate,
    );
    const data = buffer.getChannelData(0);

    // Generate shaped noise for metallic character
    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1;
    }

    this.metalSource = this.audioContext.createBufferSource();
    this.metalSource.buffer = buffer;
    this.metalSource.loop = true;
    this.metalSource.connect(this.metalFilter1);
    this.metalSource.start();

    this.metalSource.onended = () => {
      setTimeout(() => this.createMetalNoiseSource(), 10);
    };
  }

  /**
   * Create snare wire noise source for layer 3.
   */
  private createSnareNoiseSource(): void {
    if (this.snareSource) {
      this.snareSource.disconnect();
    }

    const bufferSize = this.audioContext.sampleRate * 0.5;
    const buffer = this.audioContext.createBuffer(
      1,
      bufferSize,
      this.audioContext.sampleRate,
    );
    const data = buffer.getChannelData(0);

    // Generate white noise for snare wires
    for (let i = 0; i < bufferSize; i++) {
      data[i] = rUniform(-1, 1);
    }

    this.snareSource = this.audioContext.createBufferSource();
    this.snareSource.buffer = buffer;
    this.snareSource.loop = true;
    this.snareSource.connect(this.snareFilter);
    this.snareSource.start();

    this.snareSource.onended = () => {
      console.warn("Snare source ended unexpectedly");
    };
  }

  /**
   * Play a snare drum note using persistent oscillators with click-free voice stealing.
   */
  playNote(note: MusicalNote, startTime: number, _duration: number): void {
    const velocity = note.velocity ?? 1.0;

    // Cancel previous envelope and hold current value to avoid clicks
    this.currentStopId++;
    const thisStopId = this.currentStopId;
    this.masterEnvelope.gain.cancelAndHoldAtTime(startTime);

    // Update persistent oscillator and filter parameters smoothly
    this.bodyOsc.frequency.setTargetAtTime(this.bodyFreq, startTime, 0.001);
    this.metalFilter1.frequency.setTargetAtTime(
      this.metalFreq1,
      startTime,
      0.002,
    );
    this.metalFilter2.frequency.setTargetAtTime(
      this.metalFreq2,
      startTime,
      0.002,
    );
    this.snareFilter.frequency.setTargetAtTime(
      this.snareFilterFreq,
      startTime,
      0.002,
    );

    // Update layer mix levels
    this.bodyGain.gain.setTargetAtTime(this.bodyMix, startTime, 0.001);
    this.metalGain.gain.setTargetAtTime(this.metalMix, startTime, 0.001);
    const snareMix = 1.0 - this.bodyMix - this.metalMix;
    this.snareGain.gain.setTargetAtTime(snareMix, startTime, 0.001);

    // Ramp to new attack peak from whatever the current envelope value is
    this.masterEnvelope.gain.linearRampToValueAtTime(
      velocity,
      startTime + this.attackTime,
    );

    // Exponential decay
    this.masterEnvelope.gain.exponentialRampToValueAtTime(
      0.00001,
      startTime + this.defaultDuration,
    );

    // Smooth final silence
    this.masterEnvelope.gain.setTargetAtTime(
      0,
      startTime + this.defaultDuration,
      0.001,
    );

    // Body oscillator frequency sweep (like membrane pitch drop)
    this.bodyOsc.frequency.exponentialRampToValueAtTime(
      this.bodyFreq * 0.5,
      startTime + this.bodyDecay,
    );

    // Recreate noise sources periodically to avoid staleness
    const shouldRecreateNoise = Math.random() < 0.03; // 3% chance
    if (shouldRecreateNoise) {
      setTimeout(
        () => {
          if (thisStopId === this.currentStopId) {
            this.createMetalNoiseSource();
            this.createSnareNoiseSource();
          }
        },
        (startTime - this.audioContext.currentTime) * 1000,
      );
    }
  }

  /**
   * Get valid MIDI notes for snare drum.
   */
  getValidNotes(): number[] {
    return [38]; // Only D2 snare drum note
  }

  /**
   * Clean up persistent nodes when instrument is destroyed.
   */
  onDestroy(): void {
    // Stop and disconnect persistent oscillators
    this.bodyOsc.stop();
    this.bodyOsc.disconnect();
    this.bodyGain.disconnect();

    if (this.metalSource) {
      this.metalSource.stop();
      this.metalSource.disconnect();
    }
    this.metalFilter1.disconnect();
    this.metalFilter2.disconnect();
    this.metalGain.disconnect();

    if (this.snareSource) {
      this.snareSource.stop();
      this.snareSource.disconnect();
    }
    this.snareFilter.disconnect();
    this.snareGain.disconnect();
    this.masterEnvelope.disconnect();

    super.onDestroy();
  }

  /**
   * Initialize snare drum parameters.
   */
  private initializeParameters(): void {
    this.addParameter(
      () => this.bodyFreq,
      (value) => {
        this.bodyFreq = value;
      },
      {
        key: "bodyFreq",
        label: "Body Frequency",
        min: 100,
        max: 400,
        defaultValue: 200,
        step: 10,
        unit: "Hz",
        category: "Snare",
      },
    );

    this.addParameter(
      () => this.bodyDecay,
      (value) => {
        this.bodyDecay = value;
      },
      {
        key: "bodyDecay",
        label: "Body Decay",
        min: 0.001,
        max: 0.2,
        defaultValue: 0.02,
        step: 0.005,
        unit: "s",
        category: "Snare",
      },
    );

    this.addParameter(
      () => this.bodyMix,
      (value) => {
        this.bodyMix = value;
      },
      {
        key: "bodyMix",
        label: "Body Mix",
        min: 0,
        max: 1.0,
        defaultValue: 0.3,
        step: 0.05,
        category: "Snare",
      },
    );

    this.addParameter(
      () => this.snareFilterFreq,
      (value) => {
        this.snareFilterFreq = value;
      },
      {
        key: "snareFilterFreq",
        label: "Wire Frequency",
        min: 1000,
        max: 5000,
        defaultValue: 2000,
        step: 100,
        unit: "Hz",
        category: "Snare",
      },
    );

    this.addParameter(
      () => this.snareFilterQ,
      (value) => {
        this.snareFilterQ = value;
      },
      {
        key: "snareFilterQ",
        label: "Wire Resonance",
        min: 0.5,
        max: 5,
        defaultValue: 1.5,
        step: 0.1,
        category: "Snare",
      },
    );

    this.addParameter(
      () => this.metalFreq1,
      (value) => {
        this.metalFreq1 = value;
      },
      {
        key: "metalFreq1",
        label: "Metal Freq 1",
        min: 2000,
        max: 6000,
        defaultValue: 3500,
        step: 100,
        unit: "Hz",
        category: "Snare",
      },
    );

    this.addParameter(
      () => this.metalFreq2,
      (value) => {
        this.metalFreq2 = value;
      },
      {
        key: "metalFreq2",
        label: "Metal Freq 2",
        min: 3000,
        max: 8000,
        defaultValue: 5000,
        step: 100,
        unit: "Hz",
        category: "Snare",
      },
    );

    this.addParameter(
      () => this.metalQ,
      (value) => {
        this.metalQ = value;
      },
      {
        key: "metalQ",
        label: "Metal Resonance",
        min: 5,
        max: 30,
        defaultValue: 10,
        step: 1,
        category: "Snare",
      },
    );

    this.addParameter(
      () => this.metalMix,
      (value) => {
        this.metalMix = value;
      },
      {
        key: "metalMix",
        label: "Metal Mix",
        min: 0,
        max: 1.0,
        defaultValue: 0.2,
        step: 0.05,
        category: "Snare",
      },
    );

    this.addParameter(
      () => this.attackTime,
      (value) => {
        this.attackTime = value;
      },
      {
        key: "attackTime",
        label: "Attack Time",
        min: 0.0001,
        max: 0.01,
        defaultValue: 0.001,
        step: 0.0001,
        unit: "s",
        category: "Snare",
      },
    );

    this.addParameter(
      () => this.defaultDuration,
      (value) => {
        this.defaultDuration = value;
      },
      {
        key: "duration",
        label: "Duration",
        min: 0.05,
        max: 0.8,
        defaultValue: 0.3,
        step: 0.01,
        unit: "s",
        category: "Snare",
      },
    );
  }
}
